import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import ProfileView from '../components/profile/ProfileView';
import EnhancedProfileEditor from '../components/profile/EnhancedProfileEditor';
import socialService, { ProfileDto } from '../services/socialService';

const PublicProfile: React.FC = () => {
  const { aliasName } = useParams<{ aliasName: string }>();
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewContentTab, setViewContentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (aliasName) {
      loadProfile();
    }
  }, [aliasName]);

  const loadProfile = async () => {
    if (!aliasName) return;

    try {
      setLoading(true);
      setError(null);
      const data = await socialService.getProfile(aliasName);
      setProfile(data.profile);
      setCanEdit(Boolean(data.canEdit));
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      setError('Profile not found or not accessible');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading profile...</Typography>
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Profile not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {isEditing && canEdit ? (
        <EnhancedProfileEditor
          profile={profile}
          onSave={async () => {
            await loadProfile();
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
          initialTab={viewContentTab}
          onTabChange={setViewContentTab}
        />
      ) : (
        <ProfileView
          profile={profile}
          isOwner={canEdit}
          onEdit={canEdit ? () => setIsEditing(true) : undefined}
          contentTabValue={viewContentTab}
          onContentTabChange={setViewContentTab}
        />
      )}
    </Container>
  );
};

export default PublicProfile;

